'use client';

import {
  Box,
  Flex,
  Icon,
  Text,
  Avatar,
  Popover,
  Portal,
} from '@chakra-ui/react';
import { AiOutlineUser } from 'react-icons/ai';
import { useContext, useState } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { LuEyeClosed } from 'react-icons/lu';
import { FaHeart } from 'react-icons/fa6';
import { FaChevronRight } from 'react-icons/fa6';
import { IconType } from 'react-icons';
import { useRouter } from 'next/navigation';
import UserMenuTabs from './UserMenuTabs';

type ButtonLinkProps = {
  IconReact: IconType;
  text: string;
  action: () => void;
};

const ButtonLink = ({ IconReact, action, text }: ButtonLinkProps) => {
  return (
    <Flex
      as="button"
      w="full"
      align="center"
      gap="12px"
      px="12px"
      py="10px"
      borderRadius="12px"
      cursor="pointer"
      transition="all 0.18s"
      onClick={action}
      bg="transparent"
      _hover={{ bg: 'rgba(0,176,202,0.06)' }}
    >
      <Flex
        w="32px"
        h="32px"
        borderRadius="9px"
        bg="rgba(0,176,202,0.1)"
        align="center"
        justify="center"
        flexShrink={0}
      >
        <Icon w="14px" h="14px" color="#00B0CA">
          <IconReact />
        </Icon>
      </Flex>
      <Text
        flex="1"
        fontSize="13px"
        fontWeight="600"
        color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
        textAlign="left"
      >
        {text}
      </Text>
      <Icon
        w="11px"
        h="11px"
        color={{ base: 'rgba(0,0,0,0.3)', _dark: 'rgba(255,255,255,0.3)' }}
      >
        <FaChevronRight />
      </Icon>
    </Flex>
  );
};

const UserMenu = () => {
  const [open, setOpen] = useState(false);

  const { profile, isAuthenticated, singOut } = useContext(AuthContext);

  const { push } = useRouter();

  if (profile && isAuthenticated) {
    return (
      <Popover.Root
        open={open}
        onOpenChange={(e) => setOpen(e.open)}
        closeOnInteractOutside
      >
        <Popover.Trigger asChild p="0" m="0">
          <Flex
            as="button"
            w={{ base: '36px', md: '38px' }}
            h={{ base: '36px', md: '38px' }}
            borderRadius="10px"
            bg="rgba(0,176,202,0.1)"
            border="1px solid rgba(0,176,202,0.2)"
            align="center"
            justify="center"
            cursor="pointer"
            transition="all 0.2s"
            flexShrink={0}
            _hover={{ bg: 'rgba(0,176,202,0.15)', borderColor: 'rgba(0,176,202,0.35)' }}
          >
            <Avatar.Root
              w="100%"
              h="100%"
              bg="transparent"
              borderRadius="9px"
              overflow="hidden"
            >
              <Avatar.Fallback
                name={profile.name}
                color="#00B0CA"
                bg="transparent"
                fontSize="12px"
                fontWeight="700"
              />
              <Avatar.Image src="https://" />
            </Avatar.Root>
          </Flex>
        </Popover.Trigger>
        <Portal>
          <Popover.Positioner>
          <Popover.Content
              css={{
                base: {
                  '--popover.-bg': '#fff',
                },
                _dark: {
                  '--popover.-bg': '#0A1B2B',
                },
              }}
              bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
              w="280px"
              borderRadius="20px"
              boxShadow="0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,176,202,0.08)"
              border="2px solid"
              borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
              pos="relative"
              left="20px"
              zIndex="999"
              overflow="hidden"
            >
              <Popover.Body p="0">
                {/* Profile header */}
                <Flex align="center" gap="12px" px="16px" pt="16px" pb="14px">
                  <Flex
                    w="40px"
                    h="40px"
                    borderRadius="12px"
                    bg="rgba(0,176,202,0.1)"
                    border="1.5px solid rgba(0,176,202,0.2)"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Text fontSize="14px" fontWeight="700" color="#00B0CA" lineHeight="1">
                      {profile.name.charAt(0).toUpperCase()}
                    </Text>
                  </Flex>
                  <Box>
                    <Text
                      fontSize="14px"
                      fontWeight="700"
                      color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
                      lineHeight="1.3"
                    >
                      {profile.name}
                    </Text>
                    <Text
                      fontSize="11px"
                      color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.4)' }}
                    >
                      Área de Cliente
                    </Text>
                  </Box>
                </Flex>

                {/* Divider */}
                <Box
                  h="1px"
                  bg={{ base: 'rgba(0,0,0,0.06)', _dark: 'rgba(255,255,255,0.06)' }}
                  mx="12px"
                />

                {/* Menu items */}
                <Flex flexDir="column" p="8px" gap="2px">
                  <ButtonLink
                    text="Dados Pessoais"
                    action={() => push('/AreaCliente/DadosPessoais')}
                    IconReact={AiOutlineUser}
                  />
                  <ButtonLink
                    text="Favoritos"
                    action={() => push('/AreaCliente/Favoritos')}
                    IconReact={FaHeart}
                  />
                  <ButtonLink
                    text="Terminar Sessão"
                    action={singOut}
                    IconReact={LuEyeClosed}
                  />
                </Flex>
              </Popover.Body>
            </Popover.Content>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    );
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      closeOnInteractOutside
    >
      <Popover.Trigger asChild p="0" m="0">
        <Flex
          as="button"
          w={{ base: '36px', md: '38px' }}
          h={{ base: '36px', md: '38px' }}
          borderRadius="10px"
          bg="transparent"
          border="1px solid transparent"
          align="center"
          justify="center"
          cursor="pointer"
          flexShrink={0}
          transition="all 0.2s"
          _hover={{ bg: 'rgba(0,176,202,0.07)', borderColor: 'rgba(0,176,202,0.2)' }}
        >
          <Icon
            w={{ base: '20px', md: '22px' }}
            h={{ base: '20px', md: '22px' }}
            color={{ base: '#0A1B2B', _dark: '#C8DCE8' }}
          >
            <AiOutlineUser />
          </Icon>
        </Flex>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            css={{
              base: { '--popover.-bg': '#fff' },
              _dark: { '--popover.-bg': '#0A1B2B' },
            }}
            bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
            w="350px"
            maxH="80vh"
            borderRadius="20px"
            boxShadow="0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,176,202,0.08)"
            border="1.5px solid"
            borderColor={{ base: 'rgba(0,176,202,0.12)', _dark: 'rgba(0,176,202,0.1)' }}
            zIndex="999"
          >
            <Popover.Body>
              <UserMenuTabs />
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
};

export default UserMenu;
